import React, { Fragment, useEffect, useState } from "react";
import {
  Table,
  Pagination,
  Flex,
  Button,
  Search,
  Checkbox,
  Pill,
  Loader,
  Text
} from "@theorgchartdev/storybook";
import { GetWSMgr } from "@/Utils/StateLib";
import cx from "classnames";
import { UpdatePlans } from "@/Utils/Promises";
import { GetFieldDefsFromList } from "@/Utils/DataLib";
import { CapitalizeFirstWord, SortCompareDates } from "@/Utils/DataLib";
import AboutPlanModal from "@/appComponents/Blocks/Modals/ChartExplorerModal/PlanExplorer/AboutPlanModal";
import { PlanFieldsModal } from "../../PlanFieldsModal";
import { PLACEHOLDER_TABLE_VALUE, TABLE_SELECTED_ROW_STYLE } from "@/Utils/Constants";
import { useStatusController } from "@/hooks/useStatusController";
import {
  kAdminUser,
  kUserPlanRoles,
  kDefaultPlanFields,
  enumDateTimeDisplayOptions,
  enumUpdatePlanModes
} from "@coreapp/constants";
import { QDumpPlanRFC } from "@coreapp/CoreApp/QDumpPlanRFC";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";
import { DefaultPlanObjective } from "@/Utils/Plan/ObjectivesHandler";
import { useToast } from "@/providers/ToastProvider";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import PlanApprovalStatusModal from "../../PlanApprovalStatusModal/PlanApprovalStatusModal";
import { SubmissionStatus } from "@/Utils/Plan/PlanSubmisionsTypes";
import PlanTable from "./PlanTable";

const DoDateSort = planData => {
  return [...planData].sort((a, b) => {
    const d1 = new Date().convertUTCDateToLocalDateString(
      a.modDate,
      null,
      null,
      enumDateTimeDisplayOptions.shortdatetime
    );
    const d2 = new Date().convertUTCDateToLocalDateString(
      b.modDate,
      null,
      null,
      enumDateTimeDisplayOptions.shortdatetime
    );

    return SortCompareDates(d1, d2);
  });
};

export const GetUserRole = (users, userID) => {
  for (var i in users) {
    if (users[i].name === userID) {
      return users[i].role;
    }
  }
  return "";
};

const GetActivePlanUsersList = users => {
  let usersList = [];
  for (var i in users) {
    const role = users[i].role;
    if (!kUserPlanRoles.includes(role)) {
      continue;
    }

    usersList.push(users[i].name);
  }
  return usersList;
};

const defaultFilterState = {
  active: true,
  sharedOnly: true,
  needingMyApproval: false
};

const PlanExplorer = ({ selectedPlan, setSelectedPlan, onPlanClick, openPlanBuilder }) => {
  const [filterMgr, setFilterMgr] = useState(defaultFilterState);
  const [planData, setPlanData] = useState([]);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [aboutData, setAboutData] = useState({});
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  // const [fieldsMgr, setFieldsMgr] = useState({ defaultFields: [], additionalFields: [] });
  const [searchValue, setSearchValue] = useState("");

  const [showApprovalStatusModal, setShowApprovalStatusModal] = useState(false);
  const [planSubmissionData, setPlanSubmissionData] = useState(null);

  const statusController = useStatusController();
  const { addToast } = useToast();
  const { canAccessPlanApprovals } = usePermissionCheck();

  const LoadPlansList = async () => {
    const wsMgr = GetWSMgr();
    const callbackObj = {
      HandlePlans: plans => {
        setPlanData(DoDateSort(plans));
      }
    };
    return wsMgr.GetPlans(callbackObj);
  };

  useEffect(() => {
    LoadPlansList().then(() => {
      statusController.setReady();
    });
  }, []);

  const wsMgr = GetWSMgr();
  const userInfo = wsMgr.GetUserInfo();
  const userID = userInfo.GetUserName();
  const userRole = userInfo.GetUserRole();

  const processedPlans = React.useMemo(() => {
    return planData
      .filter(plan => {
        const sharedFilter =
          !filterMgr.sharedOnly || GetActivePlanUsersList(plan.users).includes(userID);
        const searchFilter =
          searchValue.length === 0 ||
          plan.name.toLowerCase().lastIndexOf(searchValue.toLowerCase()) !== -1;
        const needingMyApprovalFilter =
          !filterMgr.needingMyApproval ||
          (plan.latestSubmission?.reviewers.some(reviewer => reviewer.username === userID) ??
            false);

        return sharedFilter && searchFilter && needingMyApprovalFilter;
      })
      .map(plan => {
        const newPlan = { ...plan };
        if (!newPlan.objective) {
          newPlan.objective = DefaultPlanObjective;
        }
        const role = GetUserRole(newPlan.users, userID);
        newPlan.role = role && !["", "disabled"].includes(role) ? appstr(role) : "";

        if (!newPlan.enablePlanApprovals) {
          newPlan.approvalStatusSortValue = "--";
        } else {
          newPlan.approvalStatusSortValue = appstr(
            newPlan.latestSubmission?.status ?? SubmissionStatus.Draft
          );
        }

        return newPlan;
      });
  }, [planData, filterMgr.sharedOnly, filterMgr.needingMyApproval, searchValue, userID]);

  const GetPlanData = () => {
    const targetPlan = planData.find(plan => {
      return plan.ID === selectedPlan;
    });
    return targetPlan ? targetPlan : null;
  };

  const GetDefaultFieldNames = () => {
    let defaultFieldDefs = GetFieldDefsFromList(kDefaultPlanFields);
    return defaultFieldDefs.map(item => {
      return item.GetFieldLabel() ? item.GetFieldLabel() : item.GetFieldName();
    });
  };

  // const AddAdditionalField = fields => {
  //   setFieldsMgr(prevState => ({ ...prevState, additionalFields: [...fields] }));
  // };

  // const CloseFieldsModal = () => {
  // setShowFieldsModal(false);
  // setFieldsMgr({ defaultFields: [], additionalFields: [] });
  // };

  const SavePlan = async (updatedPlanData, mode, message) => {
    statusController.performUpdate(async () => {
      const { success, response } = await UpdatePlans(updatedPlanData, mode);
      if (success) {
        addToast({ message: appstr("Changes have been saved"), status: "success" });
        await LoadPlansList();
      } else {
        addToast({ message: appstr("An error occurred while saving"), status: "error" });
      }
    }, message);
  };

  const DownloadPlan = planID => {
    const wsmgr = GetWSMgr();
    const accountName = wsmgr.GetAccountName();
    const username = wsmgr.GetUserName();
    const dumpPlanRFC = new QDumpPlanRFC();
    dumpPlanRFC.InitDumpPlanRFC(wsmgr);
    dumpPlanRFC.SetParams(accountName, username, "dumpPlanData", planID);
    Globals._rfcMgr.QueueRFC(dumpPlanRFC);
  };

  const HandlePlanAction = async (targetPlan, mode) => {
    let planID = parseInt(targetPlan.ID);
    if (planID === null) return;
    let curStatus = targetPlan.status;
    let updateStruct = {};
    let updateMode;
    let updateMessage = null;
    let savePlans = false;
    switch (mode) {
      case "toggleLock":
        updateStruct["ID"] = planID;
        updateStruct["status"] = curStatus !== "Locked" ? "Locked" : "Active";
        updateMode = enumUpdatePlanModes.saveplan;

        updateMessage = appstr("Locking plan");
        if (curStatus === "Locked") {
          updateMessage = appstr("Unlocking plan");
        }

        savePlans = true;
        break;
      case "duplicateplan":
        const namesList = planData.map(plan => {
          return plan.name;
        });
        const newName = namesList.GetUniqueDuplicateName(targetPlan.name);
        updateStruct["ID"] = planID;
        updateStruct["newPlanName"] = newName;
        updateMode = enumUpdatePlanModes.duplicateplan;
        updateMessage = appstr("Duplicating plan");
        savePlans = true;
        break;
      case "deleteplan":
        updateStruct["planID"] = planID;
        updateMode = enumUpdatePlanModes.deleteplan;
        updateMessage = appstr("Deleting plan");
        savePlans = true;
        break;
      case "archive":
        updateStruct["ID"] = planID;
        updateStruct["status"] = curStatus !== "Archived" ? "Archived" : "Active";
        updateMode = enumUpdatePlanModes.saveplan;
        updateMessage = appstr("Archiving plan");
        savePlans = true;
        break;
      case "download":
        DownloadPlan(planID);
        break;
      case "about":
        setShowAboutModal(true);
        setAboutData(targetPlan);
        break;
      // Temporarily disable this action as currently opened doc is used to calculated fields for PlanFieldsModal from Plan Explorer.
      // TODO: Will be handled later
      // case "editfields":
      //   const defaultFields = GetDefaultFieldNames();
      //   const additionalFields = targetPlan?.fields || { holder: [], position: [] };
      //   setSelectedPlan(planID);
      //   setFieldsMgr({
      //     defaultFields: [...defaultFields],
      //     additionalFields: [...additionalFields]
      //   });
      //   setShowFieldsModal(true);
      //   break;
      // case "saveplanfields":
      //   updateStruct["ID"] = planID;
      //   updateStruct["fields"] = fieldsMgr.additionalFields;
      //   updateMode = "saveplan";
      //   updateMessage = appstr("Saving");
      //   savePlans = true;
      //   break;
      case "updateplanusers":
        updateStruct["planID"] = planID;
        updateStruct["users"] = targetPlan.users;
        updateStruct["emailInvite"] = targetPlan.emailInvite;
        if (targetPlan.defaultRole) updateStruct["defaultRole"] = targetPlan.defaultRole;
        updateMode = mode;
        updateMessage = appstr("Saving");
        savePlans = true;
        break;
      default:
        break;
    }

    if (savePlans) await SavePlan(updateStruct, updateMode, updateMessage);
  };

  const CloseAboutModal = () => {
    setShowAboutModal(false);
  };

  const CloseApprovalStatusModal = () => {
    setShowApprovalStatusModal(false);
    setPlanSubmissionData(null);
  };

  const onOpenApprovalModal = submissionData => {
    setPlanSubmissionData(submissionData);
    setShowApprovalStatusModal(true);
  };

  const SaveRenamePlan = (newName, eventInfo) => {
    const updateStruct = { ID: parseInt(eventInfo.renamePlanID), name: newName };
    SavePlan(updateStruct, "saveplan", appstr("Renaming plan"));
  };

  const hasAnyPlanWithApprovalsEnabled = planData.some(plan => plan.enablePlanApprovals);
  const showApprovalStatusColumn = canAccessPlanApprovals && hasAnyPlanWithApprovalsEnabled;

  const onSearchInput = value => setSearchValue(value);

  return (
    <Fragment key="plans">
      <Loader.Root>
        <div className={"ocn-file-manager-file-container"}>
          <Flex direction={"column"} height={"100%"}>
            <Flex padding={"12px 16px"} alignItems={"center"} justifyContent={"space-between"}>
              <Flex width={"100%"} gap={"16px"}>
                <Button
                  onClick={openPlanBuilder}
                  iconProps={{ name: "plus" }}
                  modifier="primary"
                  className="ocn-plan-explorer-create-plan"
                  dataTestId="chart-exp-action-Create"
                >
                  {appstr("Create entity", { entity: appstr("Plan").toLowerCase() })}
                </Button>
                <div style={{ width: "40%" }} className="ocn-plan-explorer-search">
                  <Search
                    clearable
                    onInput={onSearchInput}
                    inputProps={{
                      style: { fontSize: "14px" },
                      spellCheck: false
                    }}
                    size={"medium"}
                    placeholder={appstr("Search plans")}
                    timeout={200}
                    onResetInput={() => setSearchValue("")}
                  />
                </div>
                <Flex direction={"row"} alignItems={"center"} flexWrap={"nowrap"} gap={"16px"}>
                  {userRole === kAdminUser && (
                    <Checkbox
                      key={"plan-chkbx-1"}
                      id={"plan-chkbx-1"}
                      checked={!filterMgr.sharedOnly}
                      size={"medium"}
                      label={appstr("Show plans not shared with me")}
                      data-testid="chart-mode-explorer-plans-show-unshared-checkbox"
                      onToggle={() => {
                        setFilterMgr(prevState => ({
                          ...prevState,
                          sharedOnly: !prevState.sharedOnly
                        }));
                      }}
                    />
                  )}
                  {showApprovalStatusColumn && (
                    <Checkbox
                      key={"plan-chkbx-2"}
                      id={"plan-chkbx-2"}
                      checked={filterMgr.needingMyApproval}
                      size={"medium"}
                      label={appstr("Show only plans needing my approval")}
                      data-testid="plan-needing-approval-checkbox"
                      onToggle={() => {
                        setFilterMgr(prevState => ({
                          ...prevState,
                          needingMyApproval: !prevState.needingMyApproval
                        }));
                      }}
                    />
                  )}
                </Flex>
              </Flex>
            </Flex>
            {showAboutModal && (
              <AboutPlanModal show={showAboutModal} onClose={CloseAboutModal} curPlan={aboutData} />
            )}
            <PlanTable
              allPlans={processedPlans}
              statusController={statusController}
              selectedPlan={selectedPlan}
              onPlanClick={onPlanClick}
              onPlanAction={HandlePlanAction}
              onSaveRename={SaveRenamePlan}
              showApprovalStatusColumn={showApprovalStatusColumn}
              onOpenApprovalModal={onOpenApprovalModal}
            />
          </Flex>
          {/* Temporarily disabled till support is added to handle additional fields */}
          {/* {showFieldsModal && (
            <PlanFieldsModal
              show={showFieldsModal}
              onClose={() => CloseFieldsModal()}
              onChange={AddAdditionalField}
              onSave={() => HandlePlanAction(GetPlanData(), "saveplanfields")}
              defaultFields={fieldsMgr.defaultFields}
              additionalFields={fieldsMgr.additionalFields}
            />
          )} */}
          {showApprovalStatusModal && planSubmissionData && (
            <PlanApprovalStatusModal
              show={showApprovalStatusModal}
              onClose={CloseApprovalStatusModal}
              submissionData={planSubmissionData}
            />
          )}
        </div>
      </Loader.Root>
    </Fragment>
  );
};

export default PlanExplorer;
